// src/hooks/macro/useFedRateProbabilities.ts

import { useQuery } from '@tanstack/react-query';
import { fedRateProbabilitiesQueryOptions } from '@/services/queries/macro/fedRateProbabilitiesQueries';
import type { FedRateProbabilitiesResponse } from '@/types/dashboards/macro';

export interface UseFedRateProbabilitiesOptions {
  enabled?: boolean;
  refetchInterval?: number;
}

export interface UseFedRateProbabilitiesResult {
  data: FedRateProbabilitiesResponse | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  isSuccess: boolean;
  isError: boolean;
}

/**
 * React hook for fetching Fed rate probabilities from the API
 * @param options - Configuration options for the data fetch
 * @returns Object containing data, loading state, error, and refetch function
 */
export const useFedRateProbabilities = (options: UseFedRateProbabilitiesOptions = {}): UseFedRateProbabilitiesResult => {
  const { 
    enabled = true, 
    refetchInterval,
  } = options;

  const {
    data,
    isLoading,
    error,
    refetch,
    isSuccess,
    isError,
  } = useQuery({
    ...fedRateProbabilitiesQueryOptions(),
    enabled,
    ...(refetchInterval !== undefined && { refetchInterval }),
  });

  return {
    data,
    isLoading,
    error: error as Error | null,
    refetch,
    isSuccess,
    isError,
  };
};
